import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Institution } from 'src/app/models/user';
import { ResourceItem, ResourceItemModel, ROLES } from 'src/app/models/resource';
import { first } from 'rxjs/operators';
import { ResourcesService } from '../resources.service';

/**
 * Service providing common utilities for Virtual Background functionality
 */
@Injectable({
  providedIn: 'root',
})
export class VirtualBackgroundUtilsService {
  constructor(
    private resourcesService: ResourcesService,
    private translateService: TranslateService,
  ) {}

  /**
   * Extracts virtual background folder ID from institution theme settings
   * @param institution The institution object containing theme data
   * @returns The folder ID or undefined if not set
   */
  getInstitutionVBsFolder(institution?: Institution): string | undefined {
    return institution?.theme?.virtualBackgroundSettings?.folder;
  }

  /**
   * Checks if institution virtual backgrounds are restricted
   * @param institution The institution object containing theme data
   * @returns True if restricted, false otherwise
   */
  isInstitutionVBsRestricted(institution?: Institution): boolean {
    return institution?.theme?.virtualBackgroundSettings?.isRestricted ?? false;
  }

  /**
   * Checks if institution has virtual background images
   * @param institution The institution object containing theme data
   * @returns True if has images, false otherwise
   */
  hasInstitutionVBsImages(institution?: Institution): boolean {
    return institution?.theme?.virtualBackgroundSettings?.hasImages ?? false;
  }

  /**
   * Loads institution virtual backgrounds
   * @param institution The institution object containing theme data
   * @param onSuccess Callback with loaded backgrounds
   * @param loadingState Optional BehaviorSubject to manage loading state
   */
  loadInstitutionVirtualBackgrounds(
    institution: Institution | undefined,
    onSuccess: (backgrounds: ResourceItem[]) => void,
    loadingState?: BehaviorSubject<boolean>,
  ): void {
    const folderId = this.getInstitutionVBsFolder(institution);

    if (!folderId) {
      onSuccess([]);
      return;
    }

    this.resourcesService.loadResourcesFromFolder(
      folderId,
      this.translateService.instant('Failed to load virtual backgrounds'),
      onSuccess,
      loadingState,
      true,
    );
  }

  /**
   * Deletes institution virtual backgrounds
   * @param resourceIds Array of resource IDs to delete
   * @param loadingState Optional BehaviorSubject to manage loading state
   * @returns Promise that resolves when delete completes
   */
  async deleteInstitutionVirtualBackgrounds(
    resourceIds: string[],
    loadingState?: BehaviorSubject<boolean>,
  ): Promise<void> {
    loadingState?.next(true);

    const response = await firstValueFrom(
      this.resourcesService.deleteResources(resourceIds).pipe(first()),
    );

    if (!response) {
      throw new Error('Failed to delete virtual backgrounds');
    }
  }

  // ========== Private helper methods (logical blocks) ==========

  /**
   * Creates a resource from a ResourceItem in a folder
   * @param folderId The folder ID
   * @param item The ResourceItem with url and name
   * @returns Promise that resolves when resource is created
   */
  private async _createResourceFromUrl(folderId: string, item: ResourceItem): Promise<void> {
    await firstValueFrom(
      this.resourcesService.createResource({
        url: item.url,
        name: item.name || `Virtual Background ${Date.now()}`,
        size: 0,
        acl: { visibility: ROLES.PUBLIC_GUEST, public: false },
        type: ResourceItemModel.IMAGE,
        metadata: {},
        folder: folderId,
      }),
    );
  }

  // ========== Public methods (composed from logical blocks) ==========

  /**
   * Creates a virtual background folder for the institution
   * Does not update the user - caller is responsible for updating
   * @param institutionName The name of the institution
   * @returns Promise that resolves with the folder ID
   */
  async createInstitutionVBsFolder(institutionName?: string): Promise<string> {
    const folderName = `${institutionName || 'Institution'} - Virtual Backgrounds`;
    const response = await firstValueFrom(
      this.resourcesService
        .createFolder({
          name: folderName,
          acl: { visibility: ROLES.PUBLIC_GUEST, public: false },
        })
        .pipe(first()),
    );

    const folderId = response?.body?._id;
    if (!folderId) {
      throw new Error('Failed to create folder');
    }
    return folderId;
  }

  /**
   * Gets existing folder ID or creates a new folder if it doesn't exist
   * @param institution The institution object
   * @returns Promise that resolves with the folder ID
   */
  async getOrCreateInstitutionVBsFolder(institution?: Institution): Promise<string> {
    const existingFolderId = this.getInstitutionVBsFolder(institution);
    if (existingFolderId) {
      return existingFolderId;
    }
    return this.createInstitutionVBsFolder(institution?.name);
  }

  /**
   * Creates resources from ResourceItems in a folder
   * @param folderId The folder ID
   * @param items Array of ResourceItems with url and name
   * @returns Promise that resolves when all resources are created
   */
  async createResourcesFromUrls(folderId: string, items: ResourceItem[]): Promise<void> {
    if (items.length === 0) {
      return;
    }

    await Promise.all(items.map((item) => this._createResourceFromUrl(folderId, item)));
  }
}
